/**
 * Minimal token store for the Office Dialog auth flow.
 *
 * `auth0-spa-js`'s `loginWithPopup()` doesn't work inside Word's WebView
 * because `window.open` returns null there. For the Office path we run the
 * PKCE flow ourselves via Office Dialog API, then persist the resulting
 * tokens here. The API client (`src/api/client.ts`) consults this store
 * first; if empty, it falls back to auth0-spa-js.
 *
 * Storage: localStorage. Per-origin, survives Word restart. Not syncing
 * via Office.context.document.settings because auth is user-scoped not
 * document-scoped.
 */

const KEY = 'nyayassist:auth0tokens';

export interface StoredTokens {
  accessToken: string;
  refreshToken?: string;
  idToken: string;
  /** epoch ms */
  expiresAt: number;
  scope: string;
}

export function getStoredTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StoredTokens) : null;
  } catch {
    return null;
  }
}

export function setStoredTokens(tokens: StoredTokens): void {
  localStorage.setItem(KEY, JSON.stringify(tokens));
}

export function clearStoredTokens(): void {
  localStorage.removeItem(KEY);
}

export function isTokenValid(tokens: StoredTokens, bufferSec = 60): boolean {
  return tokens.expiresAt > Date.now() + bufferSec * 1000;
}

export interface IdTokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  picture?: string;
  [k: string]: unknown;
}

/** Decode the payload of a JWT id_token without verifying signature. */
export function decodeIdToken(idToken: string): IdTokenClaims | null {
  try {
    const [, payload] = idToken.split('.');
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as IdTokenClaims;
  } catch {
    return null;
  }
}
